import {FC, useEffect, useReducer, useState} from "react";
import InnerLiquidSettings from "./innerLiquidSettings";
import { convertMMSettings, Section } from "../../utils/convertInnerMMSettings";
import {
  Currency,
  IDynamicOrderBook,
  IMMBots,
  ISpread,
  IVolumeIncrease,
  LiquidBotsData,
  LiquidBotTrades,
  MMSettingsAdminConnection,
  TradeType,
} from "./mmTypes";
import { mainColor, selectLighter } from "../../constants/colors";
import "../../index.css";
import CurrencyTable from "./balanceTable";
import { isNumber } from "../../utils/isNumberc";
import OpenTrade from "./createTrade";
import { RiskGuardPanel } from "../../components/RiskGuardPanel";
import { GateBatchControl } from "../../components/GateBatchControl";
import { useAlertService } from "../../hooks/useAlertService";
import { ProgressBar } from "../../components/ProgressBar";
import { changeBackground } from "../../hooks/onHover";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { SwapBalances } from "./swapBalances";
import {MMSettings, Person, SettingsProps} from "./types";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const getUsers = async () => {
  const response = await fetch(
    "https://lukkypro.ru/api/v1/users/get_user_list",
    {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
        "Content-Type": "application/json",
      },
    }
  );

  return await response.json();
};

const checkUser = async () => {
  const response = await fetch("https://lukkypro.ru/api/v1/auth/get_user", {
    method: "GET",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("authToken"),
    },
  });

  if (!response.ok) {
    throw new Error("Cant get order book data");
  }

  return await response.json();
};

const getDynamicOrderBook = async (connectionId: string) => {
  const response = await fetch(
    "https://lukkypro.ru/api/v1/dynamic_order_book/" + connectionId,
    {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
      },
    }
  );

  if (!response.ok) {
    throw new Error("Cant get order book data");
  }

  const data = (await response.json()) as IDynamicOrderBook;
  return data;
};

const getSpread = async (connectionId: string) => {
  const response = await fetch(
    "https://lukkypro.ru/api/v1/spreads/" + connectionId,
    {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
      },
    }
  );

  if (!response.ok) {
    throw new Error("Cant get spread data");
  }

  const data = (await response.json()) as ISpread;
  return data;
};

const getVolumeIncrease = async (connectionId: string) => {
  const response = await fetch(
    "https://lukkypro.ru/api/v1/volume_increase/" + connectionId,
    {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
      },
    }
  );

  if (!response.ok) {
    throw new Error("Cant get volume data");
  }

  const data = (await response.json()) as IVolumeIncrease;
  return data;
};

const getMMBots = async (connectionId: string) => {
  const response = await fetch(
    "https://lukkypro.ru/api/v1/bots/" + connectionId,
    {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
      },
    }
  );

  if (!response.ok) {
    throw Error("Cant get mmBots");
  }

  const data = (await response.json()) as IMMBots;
  return data;
};

const getLiquidBots = async (
  connectionId: string,
  exchange: string,
  publicKey: string,
  secreteKey: string,
  password: string
) => {
  const response = await fetch(
    "https://lukkypro.ru/api/v1/liquid_bots/" +
      connectionId +
      "?trading_sdk_type=" +
      exchange +
      "&public_key=" +
      publicKey +
      "&secret_key=" +
      secreteKey +
      "&password=" +
      password,
    {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
      },
    }
  );

  if (!response.ok) {
    throw Error("Cant get mmBots");
  }

  return (await response.json()) as LiquidBotsData;
};

const sendChanges = async (
  connectionId: string,
  fullData: MMSettings,
  showAlert: any
) => {
  const promises = [];
  promises.push(
    fetch("https://lukkypro.ru/api/v1/connections/" + connectionId + "/edit", {
      method: "POST",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(fullData.connectionSettings),
    })
  );
  promises.push(
    fetch("https://lukkypro.ru/api/v1/bots/" + connectionId + "/edit", {
      method: "POST",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(fullData.mmSettings),
    })
  );
  promises.push(
    fetch("https://lukkypro.ru/api/v1/spreads/" + connectionId + "/edit", {
      method: "POST",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(fullData.spread),
    })
  );
  promises.push(
    fetch(
      "https://lukkypro.ru/api/v1/volume_increase/" + connectionId + "/edit",
      {
        method: "POST",
        headers: {
          Authorization: "Bearer " + localStorage.getItem("authToken"),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(fullData.volume),
      }
    )
  );
  promises.push(
    fetch(
      "https://lukkypro.ru/api/v1/dynamic_order_book/" + connectionId + "/edit",
      {
        method: "POST",
        headers: {
          Authorization: "Bearer " + localStorage.getItem("authToken"),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(fullData.dynamicBook),
      }
    )
  );

  try {
    const responses = await Promise.all(promises);

    const allSuccessful = responses.every(
      (response) => response.status === 200
    );

    if (allSuccessful) {
      showAlert({
        type: "success",
        message: "All parameters saved successfully!",
      });
    } else {
      showAlert({
        type: "error",
        message: "Some parameters failed to save. Please try again.",
      });
    }
  } catch (error) {
    console.error("Error occurred during the API calls:", error);
    showAlert({
      type: "error",
      message: "Failed to save parameters. Please try again.",
    });
  }
};

const getTrades = async (
  connectionId: string,
  publicKey: string,
  privateKey: string,
  exchange: string,
  password: string
) => {
  const res = await fetch(
    "https://lukkypro.ru/api/v1/liquid_bots/" +
      connectionId +
      "/get_all_orders?trading_sdk_type=" +
      exchange +
      "&public_key=" +
      publicKey +
      "&secret_key=" +
      privateKey +
      "&password=" +
      password,
    {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
        "Content-Type": "application/json",
      },
    }
  );

  return (await res.json()) as LiquidBotTrades[];
};

const getBalance = async (
  connectionId: string,
  privateKey: string,
  publicKey: string,
  exchange: string,
  password: string
) => {
  const res = await fetch(
    "https://lukkypro.ru/api/v1/connections/" +
      connectionId +
      "/get_balance?trading_sdk_type=" +
      exchange +
      "&public_key=" +
      publicKey +
      "&secret_key=" +
      privateKey +
      "&password=" +
      password,
    {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
        "Content-Type": "application/json",
      },
    }
  );
  return await res.json();
};

const cancelSingleOrders = async (
  connectionId: string,
  privateKey: string,
  publicKey: string,
  exchange: string,
  tradeId: string,
  password: string
) => {
  const res = await fetch(
    "https://lukkypro.ru/api/v1/liquid_bots/" +
      connectionId +
      "/" +
      tradeId +
      "/cancel_one_orders?trading_sdk_type=" +
      exchange +
      "&public_key=" +
      publicKey +
      "&secret_key=" +
      privateKey +
      "&password=" +
      password,
    {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
        "Content-Type": "application/json",
      },
    }
  );

  if (!res.ok) {
    throw new Error(`Failed to cancel order ${tradeId}. Status: ${res.status}`);
  }
  return await res.json();
};

const InnerMMSettings: FC<SettingsProps> = (props) => {
  const { showAlert } = useAlertService();
  const [isMMVisible, setMMVisible] = useState(false);
  const [isLiquidVisible, setLiquidVisible] = useState(false);
  const [isCreateTradeVisible, setIsCreateTradeVisible] = useState(true);
  const [isSwapVisibleState, setIsSwapVisibleState] = useState(false);
  const [data, setData] = useState<MMSettings>({} as MMSettings);
  const [customForm, setCustomForm] = useState<Section[]>([]);
  const [openTrades, setOpenTrades] = useState<LiquidBotTrades[]>([]);
  const [openSellTrades, setOpenSellTrades] = useState<LiquidBotTrades[]>([]);
  const [openBuyTrades, setOpenBuyTrades] = useState<LiquidBotTrades[]>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isFocus, setFocus] = useState<string>("");
  const [balanceData, setBalanceData] = useState({} as Currency[]);
  const [liquidBalanceData, setLiquidBalanceData] = useState({} as Currency[]);
  const [liquidBotsData, setLiquidBots] = useState({} as LiquidBotsData);
  const [selectedTrades, setSelectedTrades] = useState<string[]>(
    [] as string[]
  );

  const [usersData, setUsersData] = useState(() => []);
  const [managerId, setManagerId] = useState<string[]>([]);

  const handleChange = (event: any) => {
    const {
      target: { value },
    } = event;
    setManagerId(
      value
    );
  };

  const createNewTradeHandler = () => {
    setIsCreateTradeVisible(!isCreateTradeVisible);
  };

  const onClickHandleSaveSettings = (e: any) => {
    e.preventDefault();
    data.connectionSettings.password = data.connectionSettings.password
      ? data.connectionSettings.password
      : "";
    data.connectionSettings.managers = managerId;
    sendChanges(data.connectionSettings.id, data, showAlert);
  };

  const onClickHandleClosePositions =
    (showAlert: any) => (e: React.MouseEvent<HTMLButtonElement>) => {
      handleClosePositions(e, showAlert);
    };

  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const handleSelectTrade = (e: any) => {
    if (e.target.checked) {
      selectedTrades?.push(e.target.id);
      console.log(selectedTrades);
    } else {
      const copy = JSON.parse(JSON.stringify(selectedTrades));
      const index = copy.indexOf(e.target.id);
      if (index > -1) {
        copy.splice(index, 1);
      }
      setSelectedTrades(copy);
    }
  };
  const handleSelectTradeDiv = (val: string) => {
    if (!selectedTrades.includes(val)) {
      selectedTrades?.push(val);
      console.log(selectedTrades);
    } else {
      const copy = JSON.parse(JSON.stringify(selectedTrades));
      const index = copy.indexOf(val);
      if (index > -1) {
        copy.splice(index, 1);
      }
      setSelectedTrades(copy);
    }
    forceUpdate();
  };

  const handleClosePositions = async (
    e: React.MouseEvent<HTMLButtonElement>,
    showAlert: any
  ) => {
    e.preventDefault();

    try {
      const results = await Promise.allSettled(
        selectedTrades.map((trade) =>
          cancelSingleOrders(
            data.connectionSettings.id,
            liquidBotsData.private_key,
            liquidBotsData.open_key,
            data.connectionSettings.exchange,
            trade,
            liquidBotsData.password
          )
        )
      );

      const hasErrors = results.some(({ status }) => status === "rejected");

      if (hasErrors) {
        showAlert({
          type: "error",
          message: "Some positions failed to close. Please try again.",
        });
      } else {
        showAlert({
          type: "success",
          message: "All positions closed successfully!",
        });
      }

      const trades = await getTrades(
        data.connectionSettings.id,
        liquidBotsData?.open_key,
        liquidBotsData?.private_key,
        data.connectionSettings.exchange,
        liquidBotsData?.password
      );

      const compare = (a: LiquidBotTrades, b: LiquidBotTrades) => {
        if (a.price < b.price) {
          return -1;
        }
        if (a.price > b.price) {
          return 1;
        }
        return 0;
      };

      const arr1 = (trades as any).orders
        .filter((val: LiquidBotTrades) => val.is_buy == TradeType.BUY)
        .sort(compare)
        .reverse() as LiquidBotTrades[];
      const arr2 = (trades as any).orders
        .filter((val: LiquidBotTrades) => val.is_buy == TradeType.SELL)
        .sort(compare) as LiquidBotTrades[];

      setOpenTrades((trades as any).orders as LiquidBotTrades[]);
      setOpenSellTrades(arr2);
      setOpenBuyTrades(arr1);
    } catch (error) {
      console.error("Critical error while closing positions:", error);

      showAlert({
        type: "error",
        message: "Failed to close positions due to a critical error.",
      });
    }
  };

  const cancelAllOrders = () => {
    alert("Nope");
  };

  useEffect(() => {
    (async () => {
      const connectionSettings: MMSettingsAdminConnection = JSON.parse(
        JSON.stringify(props.data)
      );
      let mmSettings;
      let volume;
      let spread;
      let dynamicBook;
      let user;
      let liquidBots: any;
      let managers;
      let isError = false;

      try {
        liquidBots = await getLiquidBots(
          connectionSettings.id,
          connectionSettings.exchange,
          connectionSettings.open_key,
          connectionSettings.private_key,
          connectionSettings.password
        );
        setLiquidBots(liquidBots as LiquidBotsData);
      } catch (error) {
        console.log(error);
      }

      try {
        const balance = await getBalance(
          connectionSettings.id,
          connectionSettings.private_key,
          connectionSettings.open_key,
          connectionSettings.exchange,
          connectionSettings.password
        );

        setBalanceData(balance.balance_response.reverse() as Currency[]);

        if (!liquidBots) {
          throw new Error("No liquid bot found");
        }
        const liquidBalance = await getBalance(
          connectionSettings.id,
          liquidBots.private_key,
          liquidBots.open_key,
          connectionSettings.exchange,
          connectionSettings.password
        );

        console.log(liquidBalance);

        setLiquidBalanceData(
          liquidBalance.balance_response.reverse() as Currency[]
        );
      } catch (error) {
        console.log(error);
      }

      try {
        if (!liquidBots) {
          throw new Error("Liquid bot not found");
        }

        const trades = await getTrades(
          connectionSettings.id,
          liquidBots?.open_key,
          liquidBots?.private_key,
          connectionSettings.exchange,
          liquidBots?.password
        );

        const compare = (a: LiquidBotTrades, b: LiquidBotTrades) => {
          if (a.price < b.price) {
            return -1;
          }
          if (a.price > b.price) {
            return 1;
          }
          return 0;
        };

        const arr1 = (trades as any).orders
          .filter((val: LiquidBotTrades) => val.is_buy == TradeType.BUY)
          .sort(compare)
          .reverse() as LiquidBotTrades[];
        const arr2 = (trades as any).orders
          .filter((val: LiquidBotTrades) => val.is_buy == TradeType.SELL)
          .sort(compare) as LiquidBotTrades[];

        setOpenTrades((trades as any).orders as LiquidBotTrades[]);
        setOpenSellTrades(arr2);
        setOpenBuyTrades(arr1);
      } catch (error) {
        console.log(error);
      }

      try {
        user = await checkUser();
        mmSettings = await getMMBots(connectionSettings.id);
        volume = await getVolumeIncrease(connectionSettings.id);
        console.log(volume);
        spread = await getSpread(connectionSettings.id);
        dynamicBook = await getDynamicOrderBook(connectionSettings.id);
        managers = await getUsers();
      } catch (error) {
        isError = true;
        console.log(error);
      }

      if (!mmSettings || !volume || !spread || !dynamicBook) {
        isError = true;
      }

      console.log(isError);

      console.log(connectionSettings)

      if (!isError) {
        const fullData = {
          connectionSettings,
          mmSettings,
          spread,
          volume,
          dynamicBook,
        } as unknown as MMSettings;
        setIsAdmin(user.role == "ADMIN");
        setData(fullData);
        setCustomForm(convertMMSettings(fullData));
        setUsersData(managers);
        setManagerId(fullData.connectionSettings.managers);
        setIsLoading(false);
      } else {
        setIsError(true);
        setIsLoading(false);
      }
    })();
  }, [props.data]);

  const changeValueHandler = (value: any, name: string, val: any) => {
    let updName = "";
    let path = "";

    if (name.includes("connectionSettings")) {
      updName = name.replace("connectionSettings_", "");
      path = "connectionSettings";
    }
    if (name.includes("mmSettings")) {
      updName = name.replace("mmSettings_", "");
      path = "mmSettings";
    }
    if (name.includes("spread")) {
      updName = name.replace("spread_", "");
      path = "spread";
    }
    if (name.includes("volumes")) {
      updName = name.replace("volumes_", "");
      path = "volume";
    }
    if (name.includes("dynamicBook")) {
      updName = name.replace("dynamicBook_", "");
      path = "dynamicBook";
    }

    if (parseFloat(value) < 0) {
      val.preventDefault();
    }

    //@ts-ignore
    data[path as keyof MMSettings][updName] = isNumber(value)
      ? Math.abs(value)
      : value;

    setData(data);
  };

  const sortSell = () => {
    const dc = JSON.parse(JSON.stringify(openSellTrades));
    setOpenSellTrades(dc.reverse());
  };

  const sortBuy = () => {
    const dc = JSON.parse(JSON.stringify(openBuyTrades));
    setOpenBuyTrades(dc.reverse());
  };

  const refreshBalances = async () => {
    try {
      const _balance = getBalance(
        data.connectionSettings.id,
        data.connectionSettings.private_key,
        data.connectionSettings.open_key,
        data.connectionSettings.exchange,
        data.connectionSettings.password
      );

      const _liquidBalance = getBalance(
        data.connectionSettings.id,
        liquidBotsData.private_key,
        liquidBotsData.open_key,
        data.connectionSettings.exchange,
        data.connectionSettings.password
      );

      const [balance, liquidBalance] = await Promise.all([
        _balance,
        _liquidBalance,
      ]);
      setBalanceData(balance.balance_response.reverse() as Currency[]);
      setLiquidBalanceData(
        liquidBalance.balance_response.reverse() as Currency[]
      );

      showAlert({
        type: "success",
        message: "Balances loaded successfully.",
      });
    } catch (error) {
      showAlert({
        type: "error",
        message: "Failed to load balances.",
      });
      console.log(error);
    }
  };

  return (
    <div
      className="rounded-[20px] shadow-[5px_10px_10px]"
      style={{
        width: "75vw",
        backgroundColor: "white",
        margin: "auto",
        padding: "20px 20px 20px 20px",
        border: "1px black solid",
        maxHeight: "75vh",
        overflowY: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignContent: "space-between",
          justifyContent: "space-around",
          marginBottom: "5vh",
        }}
      >
        <div
          onMouseOver={(e) => changeBackground(e, mainColor + "1A")}
          onMouseLeave={(e) => changeBackground(e, "transparent")}
          className="cursor-pointer"
          style={{
            borderRadius: "10px",
            padding: "1px",
            textAlign: "center",
            width: "33%",
          }}
          onClick={() => {
            if (isLiquidVisible || isMMVisible) {
              setMMVisible(false);
              setLiquidVisible(false);
              // setIsCreateTradeVisible(false);
            } else {
              props.return({
                id: "",
                name: "",
                email: "",
                pairs: [],
                lastSeenOnline: "",
              });
            }
          }}
        >
          ←
        </div>
        <button
          className="cursor-pointer"
          onClick={() => {
            setMMVisible(!isMMVisible);
            setLiquidVisible(false);
            setIsSwapVisibleState(false);
          }}
          style={{
            backgroundColor: "transparent",
            border: "none",
            borderRadius: "10px",
            width: "33%",
          }}
          onMouseOver={(e) => changeBackground(e, mainColor + "1A")}
          onMouseLeave={(e) => changeBackground(e, "transparent")}
        >
          MM Settings
        </button>
        <button
          className="cursor-pointer"
          onClick={() => {
            setMMVisible(false);
            setLiquidVisible(!isLiquidVisible);
            setIsSwapVisibleState(false);
          }}
          style={{
            backgroundColor: "transparent",
            border: "none",
            borderRadius: "10px",
            width: "33%",
          }}
          onMouseOver={(e) => changeBackground(e, mainColor + "1A")}
          onMouseLeave={(e) => changeBackground(e, "transparent")}
        >
          Liquid Settings
        </button>
        <button
          className="cursor-pointer"
          onClick={() => {
            setMMVisible(false);
            setIsSwapVisibleState(!isSwapVisibleState);
            setLiquidVisible(false);
          }}
          style={{
            backgroundColor: "transparent",
            border: "none",
            borderRadius: "10px",
            width: "33%",
          }}
          onMouseOver={(e) => changeBackground(e, mainColor + "1A")}
          onMouseLeave={(e) => changeBackground(e, "transparent")}
        >
          Swap balances
        </button>
      </div>
      {isError ? (
        <div style={{ marginTop: "10px" }}>
          Failed to load data. Re-login or contact support
        </div>
      ) : isLoading ? (
        <ProgressBar />
      ) : (
        <InnerLiquidSettings
          pair={data.connectionSettings.pair_name}
          visState={isLiquidVisible}
          exchange={data.connectionSettings.exchange}
          data={liquidBotsData}
          return={props.return}
        />
      )}
      {isError ? (
        <div style={{ marginTop: "10px" }}>
          Failed to load data. Re-login or contact support
        </div>
      ) : isLoading ? (
        <div />
      ) : (
        <>
        <RiskGuardPanel key={data.connectionSettings.id} connectionId={data.connectionSettings.id} exchange={data.connectionSettings.exchange} />
        <GateBatchControl key={`gate-batch-${data.connectionSettings.id}`} connectionId={data.connectionSettings.id} exchange={data.connectionSettings.exchange} />
        <OpenTrade
          visState={isCreateTradeVisible}
          data={{
            id: data.connectionSettings.id,
            exchange: data.connectionSettings.exchange,
            private_key: liquidBotsData.private_key,
            password: liquidBotsData.password,
            open_key: liquidBotsData.open_key,
          }}
        />
        </>
      )}

      {isError ? (
        <div></div>
      ) : isLoading ? (
        <div />
      ) : (
        <SwapBalances
          visState={isSwapVisibleState}
          connectionId={props.data.id}
        />
      )}

      {isError ? (
        <div></div>
      ) : isLoading ? (
        <div />
      ) : (
        <form
          style={{
            gridTemplateColumns: "2fr 1fr",
            gap: "1rem",
            display: isMMVisible ? "grid" : "none",
          }}
        >
          <form>
            {customForm.map((section, sectionIndex) => (
              <div
                style={
                  (section.isAdmin && isAdmin) ||
                  (!section.isAdmin && !isAdmin) ||
                  (!section.isAdmin && isAdmin)
                    ? {}
                    : { display: "none" }
                }
                key={sectionIndex}
              >
                <h3>{section.section}</h3>
                {section.fields.map((field, index) => (
                  <div key={index} style={inputContainerStyle}>
                    <label htmlFor={field.id}>{field.label}</label>
                    {field.type === "select" ? (
                      <select
                        id={field.id}
                        defaultValue={field.value as string}
                        onChange={(val) => {changeValueHandler(val.target.value, val.target.id, val);}}
                      >
                        <option value="BUY">BUY</option>
                        <option value="SELL">SELL</option>
                        <option value="BOTH">BOTH</option>
                      </select>
                    ) : field.id == "connectionSettings_manager_id" ? (
                      <div>
                        <FormControl sx={{ m: 1, width: 300 }}>
                          <InputLabel id="demo-multiple-name-label">
                            Name
                          </InputLabel>
                          <Select
                            labelId="demo-multiple-name-label"
                            id="demo-multiple-name"
                            multiple
                            value={managerId}
                            onChange={handleChange}
                            input={<OutlinedInput label="Name" />}
                            MenuProps={MenuProps}
                          >
                            {usersData.map(
                              (name: {
                                id: string;
                                role: string;
                                name: string;
                              }) => (
                                <MenuItem
                                  key={name.id}
                                  value={name.id}
                                >
                                  {name.role + " " + name.name}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                      </div>
                    ) : (
                      <input
                        type={field.type}
                        id={field.id}
                        defaultValue={
                          field.type === "checkbox"
                            ? undefined
                            : (field.value as string | number)
                        }
                        defaultChecked={
                          field.type === "checkbox"
                            ? (field.value as boolean)
                            : undefined
                        }
                        style={
                          isFocus == field.label
                            ? {
                                width: "25vw",
                                outline: "none",
                                border: "none",
                                borderBottom: "1px solid " + selectLighter,
                              }
                            : {
                                width: "25vw",
                                border: "none",
                                borderBottom: "1px solid " + mainColor,
                              }
                        }
                        onFocus={() => setFocus(field.label)}
                        onBlur={() => setFocus(field.label)}
                        onChange={(val) => {
                          const value = field.type === "checkbox" ? val.target.checked : val.target.value;
                          console.log(value)
                          changeValueHandler(value, val.target.id, val);
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </form>

          <button
            className="cursor-pointer"
            style={{
              position: "sticky",
              top: 0,
              height: "50px",
              backgroundColor: "transparent",
              border: "1px solid black",
              borderRadius: "5px",
              marginTop: "25px",
              marginBottom: "25px",
            }}
            onClick={(e) => onClickHandleSaveSettings(e)}
            onMouseOver={(e) => changeBackground(e, mainColor + "1A")}
            onMouseLeave={(e) => changeBackground(e, "transparent")}
          >
            Save changes
          </button>
        </form>
      )}
      {isError ? (
        <div></div>
      ) : isLoading ? (
        <div />
      ) : (
        <div
          style={{
            display:
              isMMVisible || isLiquidVisible || isSwapVisibleState
                ? "none"
                : "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ marginBottom: "30px  " }}>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "30px",
              }}
            >
              <div>
              {data.connectionSettings.exchange} - {data.connectionSettings.pair_name}
              </div>
              MM balance
              <button
                className="cursor-pointer"
                style={{
                  backgroundColor: "transparent",
                  border: "1px solid black",
                  borderRadius: "10px",
                  height: "30px",
                  marginLeft: "30px",
                }}
                onMouseOver={(e) => changeBackground(e, mainColor + "1A")}
                onMouseLeave={(e) => changeBackground(e, "transparent")}
                onClick={() => refreshBalances()}
              >
                Refresh balances
              </button>
            </div>
            <CurrencyTable data={balanceData} />
            <div style={{ marginTop: "20px" }}></div>
            Liquid balance
            <CurrencyTable data={liquidBalanceData} />
          </div>
          <div style={{ margin: "auto" }}>Open trades</div>
          <button
            onClick={() => {
              createNewTradeHandler();
            }}
            className="cursor-pointer"
            style={{
              backgroundColor: "transparent",
              border: "1px solid black",
              borderRadius: "10px",
              marginTop: "25px",
              marginBottom: "25px",
              height: "50px",
            }}
            onMouseOver={(e) => changeBackground(e, mainColor + "1A")}
            onMouseLeave={(e) => changeBackground(e, "transparent")}
          >
            Create Trade
          </button>
          <div
            style={{
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
              display: "grid",
            }}
          >
            {openTrades.length > 0 ? (
              <div style={styles.container}>
                <div style={styles.left}>
                  <h3 className="cursor-pointer" onClick={() => sortSell()}>
                    SELL
                  </h3>
                  {openSellTrades.map((val, index) => (
                    <div
                      id={val.id}
                      onClick={() => handleSelectTradeDiv(val.id)}
                      className="rounded-[20px] shadow-[5px_10px_10px] cursor-pointer"
                      style={{
                        minWidth: "25vw",
                        display: "flex",
                        justifyContent: "space-evenly",
                        marginTop: "20px",
                        backgroundColor: "white",
                        padding: "10px",
                        border: "1px black solid",
                      }}
                    >
                      <div>
                        amount: <p>{val.amount}</p>
                      </div>
                      <div>
                        side: <p>{val.is_buy}</p>
                      </div>
                      <div>
                        price: <p>{val.price}</p>
                      </div>
                      <input
                        checked={selectedTrades.includes(val.id)}
                        className="cursor-pointer"
                        onChange={(e) => handleSelectTrade(e)}
                        id={val.id}
                        type="checkbox"
                        name=""
                      />
                    </div>
                  ))}
                </div>

                <div style={styles.right}>
                  <h3 className="cursor-pointer" onClick={() => sortBuy()}>
                    BUY
                  </h3>
                  {openBuyTrades.map((val, index) => (
                    <div
                      id={val.id}
                      onClick={() => handleSelectTradeDiv(val.id)}
                      className="rounded-[20px] shadow-[5px_10px_10px] cursor-pointer"
                      style={{
                        minWidth: "25vw",
                        display: "flex",
                        justifyContent: "space-evenly",
                        marginTop: "20px",
                        backgroundColor: "white",
                        padding: "10px",
                        border: "1px black solid",
                      }}
                    >
                      <div>
                        amount: <p>{val.amount}</p>
                      </div>
                      <div>
                        side: <p>{val.is_buy}</p>
                      </div>
                      <div>
                        price: <p>{val.price}</p>
                      </div>
                      <input
                        checked={selectedTrades.includes(val.id)}
                        className="cursor-pointer"
                        onChange={(e) => handleSelectTrade(e)}
                        id={val.id}
                        type="checkbox"
                        name=""
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div>Unable to load trades</div>
            )}

            <div
              style={{
                marginTop: "30px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-around",
                  position: "sticky",
                  top: 0,
                }}
              >
                <button
                  className="cursor-pointer"
                  style={{
                    paddingTop: "10px",
                    paddingBottom: "10px",
                    backgroundColor: "transparent",
                    border: "1px solid black",
                    borderRadius: "10px",
                  }}
                  onMouseOver={(e) => changeBackground(e, mainColor + "1A")}
                  onMouseLeave={(e) => changeBackground(e, "transparent")}
                  onClick={onClickHandleClosePositions(showAlert)}
                >
                  close selected
                </button>
                <button
                  className="cursor-pointer"
                  style={{
                    paddingTop: "10px",
                    paddingBottom: "10px",
                    backgroundColor: "transparent",
                    border: "1px solid black",
                    borderRadius: "10px",
                  }}
                  onMouseOver={(e) => changeBackground(e, mainColor + "1A")}
                  onMouseLeave={(e) => changeBackground(e, "transparent")}
                  onClick={() => {
                    cancelAllOrders();
                  }}
                >
                  close all
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const inputContainerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
};
const styles = {
  container: {
    display: "flex",
    justifyContent: "space-between",
    padding: "20px",
  } as React.CSSProperties,
  left: {
    flex: 1,
    textAlign: "center",
    borderRight: "1px solid #ccc",
    paddingRight: "10px",
  } as React.CSSProperties,
  right: {
    flex: 1,
    textAlign: "center",
    paddingLeft: "10px",
  } as React.CSSProperties,
  card: {
    margin: "10px 0",
    padding: "10px",
    backgroundColor: "#f9f9f9",
    borderRadius: "4px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  } as React.CSSProperties,
};
export default InnerMMSettings;
